import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock, CheckCircle, AlertCircle, XCircle, Users, Timer, TrendingUp, ArrowRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
  const { user } = useAuth();
  const socket = useSocket();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [myHistory, setMyHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = ['admin', 'manager'].includes(user?.role);

  const fetchData = async () => {
    try {
      const promises = [
        api.get('/attendance/today'),
        api.get('/attendance/my-history')
      ];
      if (isAdmin) {
        promises.push(api.get('/attendance/company/stats'));
        promises.push(api.get('/attendance/company/today'));
      }
      const results = await Promise.all(promises);
      setTodayAttendance(results[0].data);
      setMyHistory(results[1].data.slice(0, 7));
      if (isAdmin) {
        setStats(results[2].data);
        setRecentActivity(results[3].data.slice(0, 8));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!socket || !isAdmin) return;
    socket.on('attendance_update', () => fetchData());
    return () => socket.off('attendance_update');
  }, [socket, isAdmin]);

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Bonjour' : now.getHours() < 18 ? 'Bon après-midi' : 'Bonsoir';

  const chartData = myHistory.map(a => ({
    date: format(new Date(a.date), 'dd/MM'),
    heures: ((a.totalMinutes || 0) / 60).toFixed(1)
  })).reverse();

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <Clock className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-3" />
        <p className="text-gray-500">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">{greeting}, {user?.firstName} 👋</h1>
        <p className="text-gray-500 mt-1">{format(now, 'EEEE dd MMMM yyyy', { locale: fr })}</p>
      </div>

      {/* Carte statut du jour */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-200 text-sm font-medium mb-1">Statut aujourd'hui</p>
            {!todayAttendance ? (
              <>
                <h2 className="text-2xl font-bold mb-1">Non pointé</h2>
                <p className="text-blue-200 text-sm">Vous n'avez pas encore pointé</p>
              </>
            ) : todayAttendance.clockIn && !todayAttendance.clockOut ? (
              <>
                <h2 className="text-2xl font-bold mb-1">Au bureau</h2>
                <p className="text-blue-200 text-sm">
                  Entrée à {format(new Date(todayAttendance.clockIn), 'HH:mm')}
                  {todayAttendance.isLate && ` · ${todayAttendance.lateMinutes} min de retard`}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-1">Journée terminée</h2>
                <p className="text-blue-200 text-sm">
                  {((todayAttendance.totalMinutes || 0) / 60).toFixed(1)}h travaillées
                </p>
              </>
            )}
          </div>
          <Link to="/clock"
            className="bg-white text-blue-600 px-5 py-3 rounded-xl font-semibold text-sm hover:bg-blue-50 transition flex items-center gap-2">
            Pointer <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Stats admin */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, icon: Users, color: 'blue', bg: 'bg-blue-50' },
            { label: 'Présents', value: stats.present, icon: CheckCircle, color: 'green', bg: 'bg-green-50' },
            { label: 'En retard', value: stats.late, icon: AlertCircle, color: 'yellow', bg: 'bg-yellow-50' },
            { label: 'Absents', value: stats.total - stats.present, icon: XCircle, color: 'red', bg: 'bg-red-50' }
          ].map(item => (
            <div key={item.label} className={`${item.bg} rounded-2xl p-5`}>
              <item.icon className={`w-6 h-6 text-${item.color}-500 mb-3`} />
              <p className="text-3xl font-bold text-gray-800">{item.value}</p>
              <p className="text-gray-500 text-sm mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className={`grid ${isAdmin ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-6`}>
        {/* Graphique mes heures */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" /> Mes heures cette semaine
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorH" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Area type="monotone" dataKey="heures" stroke="#2563EB" fill="url(#colorH)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Activité récente (admin) */}
        {isAdmin && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-500" /> Activité en temps réel
            </h3>
            <div className="space-y-3">
              {recentActivity.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Aucune activité aujourd'hui</p>
              ) : recentActivity.map(record => (
                <div key={record.id} className="flex items-center gap-3">
                  {record.user?.avatar
                    ? <img src={record.user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                    : <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                        {record.user?.firstName?.[0]}{record.user?.lastName?.[0]}
                      </div>
                  }
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">
                      {record.user?.firstName} {record.user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {record.clockIn ? `Entrée ${format(new Date(record.clockIn), 'HH:mm')}` : ''}
                      {record.clockOut ? ` · Sortie ${format(new Date(record.clockOut), 'HH:mm')}` : ''}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    record.status === 'present' ? 'bg-green-100 text-green-700' :
                    record.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {record.status === 'present' ? 'Présent' : record.status === 'late' ? 'En retard' : record.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
